/**
 * Copyright (c) 2019, Tidepool Project
 *
 * This program is free software; you can redistribute it and/or modify it under
 * the terms of the associated License, which is identical to the BSD 2-Clause
 * License as published by the Open Source Initiative at opensource.org.
 *
 * This program is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS
 * FOR A PARTICULAR PURPOSE. See the License for more details.
 *
 * You should have received a copy of the License along with this program; if
 * not, you can obtain one from Tidepool Project at tidepool.org.
 */

import React, { useState } from 'react';
import TidepoolRx from './tidepoolRx.js';
import ProgressBar from './progressBar.js';
import PatientInfoForm from './PatientInfoForm.js';
import PatientAccountSection from './PatientAccountSection';
import PatientProfileSection from './PatientProfileSection';
import TherapySettingsSection from './TherapySettingsSection';
import FormResponseConfirmationSection from './FormResponseConfirmationSection';
import PrescriptionPlan from './PrescriptionPlan.js';
import LoopSettings from './LoopSettings.js';
import TransferLoopSettings from './TransferLoopSettings.js';
import SettingsVerification from './SettingsVerification.js';
import SettingsCalc from '../../../views/SettingsCalc';
import { MuiThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import BackButton from '../Button';
import Done from './Done.js';
import {
  Button,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { NavigateBefore, NavigateNext, ExitToApp } from '@material-ui/icons';
import { withFormik } from 'formik';
import * as _ from '../../../data/constants.js';
import useFormValues from '../../../resources/CustomHooks';
var short = require('short-uuid');

const useStyles = makeStyles(theme => ({
  '@global': {
    body: {
      marginTop: theme.spacing(12),
      backgroundColor: theme.palette.common.white,
      display: 'flex',
      flexDirection: 'column',
      'margin-left': 'auto',
      'margin-right': 'auto',
      width: '100%'
    }
  },
  paper: {
    marginTop: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    'margin-left': 'auto',
    'margin-right': 'auto',
    width: '100%'
  },
  backButton: {
    margin: theme.spacing(3, 0, 2),
    'margin-right': 'auto'
  },
  nextSubmit: {
    margin: theme.spacing(3, 0, 2),
    'margin-left': 'auto'
  },
  bottomButtons: {
    display: 'flex'
  }
}));

/*
 * This component controls the entire form. Formik is used on this component, and valurds just get passed around through other child components
 * The Form Body is a multi-step from which has dynamic validation provided through Formik
 */

const FormBody = props => {
  const classes = useStyles();
  const [useCalc, setUseCalc] = useState(false);
  const { values, touched, errors, handleChange, handleSubmit, setFieldValue, setValues } = props;
  console.log('Values');
  console.log(values);
  const theme = createMuiTheme({
    palette: {
      primary: {
        light: '#fff',
        main: '#627CFF',
        dark: '#4f6a92'
      },
      secondary: {
        main: '#f44336'
      },
      text: {
        primary: '#4f6a92',
        secondary: '#4f6a92'
      }
    },
    typography: {
      useNextVariants: true
    }
  });
  props.changeNavVisibility(false);

  return (
    <MuiThemeProvider theme={theme}>
      <Container>
        <div className={classes.body}>
          <ProgressBar activeSection={values.activeSection} setFieldValue={setFieldValue} />
          <div className={classes.paper}>
            {values.activeSection === 0 && (
              <PatientAccountSection
                handleSubmit={handleSubmit}
                handleChange={handleChange}
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
              />
            )}
            {values.activeSection === 1 && (
              <PatientProfileSection
                handleSubmit={handleSubmit}
                handleChange={handleChange}
                values={values}
                errors={errors}
                touched={touched}
                setFieldValue={setFieldValue}
              />
            )}
            {values.activeSection === 2 && (
              <div>
                {/*    <SettingsCalc setValues={props.setValues} prevValues={values} />  */}
                <TherapySettingsSection
                  setFieldValue={setFieldValue}
                  handleSubmit={handleSubmit}
                  handleChange={handleChange}
                  values={values}
                  errors={errors}
                  touched={touched}
                />
              </div>
            )}

            {values.activeSection === 3 && (
              <FormResponseConfirmationSection
                setFieldValue={setFieldValue}
                handleSubmit={handleSubmit}
                handleChange={handleChange}
                values={values}
                errors={errors}
                touched={touched}
                updateData={props.updateData}
                data={props.data}
              />
            )}
            {values.activeSection >= 4 && <Done firstName={values.firstName} />}
          </div>
          {/*(values.activeSection > 7 && (
            <div className={classes.bottomButtons}>
              <div className={classes.backButton}>
                <Button
                  name="goBack"
                  variant="outlined"
                  color="primary"
                  className={classes.backButton}
                  disabled={props.values.activeSection === 0}
                  onClick={() => {
                    if (props.values.therapySetting === 'cpt' && props.values.activeSection === 3) {
                      props.setFieldValue('activeSection', props.values.activeSection - 2);
                    } else {
                      props.setFieldValue('activeSection', props.values.activeSection - 1);
                    }
                  }}
                >
                  <NavigateBefore />
                  Previous Step
                </Button>
              </div>
              <div className={classes.nextSubmit}>
                {values.activeSection !== 2 ? (
                  <Button
                    type="submit"
                    name="submit"
                    variant="contained"
                    color="primary"
                    className={classes.nextSubmit}
                    onClick={handleSubmit}
                    disabled={values.activeSection === 4 && !values.acknowledgedTerms}
                  >
                    {values.activeSection !== 4 ? 'Save and Continue' : 'Send Prescription '}
                    <NavigateNext />
                  </Button>
                ) : (
                  values.computedISF > 0 &&
                  values.tdd > 0 &&
                  values.computedInsulinToCarbRatio > 0 && (
                    <Button
                      type="submit"
                      name="submit"
                      variant="contained"
                      color="primary"
                      className={classes.nextSubmit}
                      onClick={() => props.setFieldValue('activeSection', 3)}
                      disabled={values.activeSection === 4 && !values.acknowledgedTerms}
                    >
                      Save and Continue
                      <NavigateNext />
                    </Button>
                  )
                )}
              </div>
            </div>
          */}
          {values.activeSection === 5 && (
            <div className={classes.bottomButtons}>
              <div className={classes.nextSubmit}>
                <BackButton to="/" name="backToPortal">
                  Back to Portal
                </BackButton>
              </div>
            </div>
          )}
        </div>
      </Container>
    </MuiThemeProvider>
  );
};

// This is the React app using the formik extension, formik is acting as a state management tool and helps with validation
const EnhancedFormBody = withFormik({
  mapPropsToValues({
    user_id,
    firstName,
    lastName,
    birthday,
    mrn,
    email,
    sex,
    weight,
    yearOfDiagnosis,
    phoneNumber,
    street1Address,
    street2Address,
    cityAddress,
    stateAddress,
    zipcodeAddress,
    basalArray,
    bolusArray,
    sensitivityArray,
    suspendThreshold,
    correctionRange,
    insulinModel,
    maxBasal,
    maxBolus,
    training,
    therapySetting,
    loopMode,
    acknowledgedTerms,
    timeSubmitted,
    activeSection,
    useCalculator,
    tdd,
    computedInsulinToCarbRatio,
    computedISF,
    accountType,
    hasIosDevice,
    sendLinkToPhone,
    gender,
    rxConfirmed,
    hasDexcomRx,
    hasHorizonRx
  }) {
    return {
      user_id: user_id || short.uuid(),
      firstName: firstName || '',
      lastName: lastName || '',
      birthday: birthday || '',
      mrn: mrn || '',
      email: email || '',
      sex: sex || '',
      weight: weight || '',
      yearOfDiagnosis: yearOfDiagnosis || '',
      phoneNumber: phoneNumber || '',
      street1Address: street1Address || '',
      street2Address: street2Address || '',
      cityAddress: cityAddress || '',
      zipcodeAddress: zipcodeAddress || '',
      stateAddress: stateAddress || '',
      basalArray: basalArray || '',
      bolusArray: bolusArray || '',
      sensitivityArray: sensitivityArray || '',
      suspendThreshold: suspendThreshold || 80,
      correctionRange: correctionRange || '',
      insulinModel: insulinModel || '',
      maxBasal: maxBasal || 5,
      maxBolus: maxBolus || 10,
      training: training || '',
      therapySetting: therapySetting || 'initial',
      loopMode: loopMode || 'clm',
      acknowledgedTerms: acknowledgedTerms || false,
      timeSubmitted: timeSubmitted || new Date(),
      activeSection: activeSection || 0,
      tdd: tdd || 0,
      computedInsulinToCarbRatio: computedInsulinToCarbRatio || 0,
      computedISF: computedISF || 0,
      accountType: accountType || '',
      hasIosDevice: hasIosDevice || '',
      sendLinkToPhone: sendLinkToPhone || false,
      gender: gender || '',
      rxConfirmed: rxConfirmed || false,
      hasDexcomRx: hasDexcomRx || false,
      hasHorizonRx: hasHorizonRx || false

      /* user_id: user_id || short.uuid(),
      firstName: firstName || 'Daniel',
      lastName: lastName || 'Watson',
      birthday: birthday || '2020-03-10',
      mrn: mrn || '7777777',
      email: email || 'daniel@monkrobot.com',
      sex: sex || '',
      weight: weight || '',
      yearOfDiagnosis: yearOfDiagnosis || '',
      phoneNumber: phoneNumber || '(202) 436-6418',
      street1Address: street1Address || '',
      street2Address: street2Address || '',
      cityAddress: cityAddress || '',
      zipcodeAddress: zipcodeAddress || '',
      stateAddress: stateAddress || '',
      basalArray: basalArray || '',
      bolusArray: bolusArray || '',
      sensitivityArray: sensitivityArray || '',
      suspendThreshold: suspendThreshold || 80,
      correctionRange: correctionRange || '',
      insulinModel: insulinModel || '',
      maxBasal: maxBasal || 5,
      maxBolus: maxBolus || 10,
      training: training || 'in-person',
      therapySetting: therapySetting || 'initial',
      loopMode: loopMode || 'clm',
      acknowledgedTerms: acknowledgedTerms || false,
      timeSubmitted: timeSubmitted || new Date(),
      activeSection: activeSection || 0,
      tdd: tdd || 0,
      computedInsulinToCarbRatio: computedInsulinToCarbRatio || 0,
      computedISF: computedISF || 0,
      accountType: accountType || 'patient',
      hasIosDevice: hasIosDevice || 'yes',
      sendLinkToPhone: sendLinkToPhone || false,
      gender: gender || 'female',
      rxConfirmed: rxConfirmed || false,
      hasDexcomRx: hasDexcomRx || false,
      hasHorizonRx: hasHorizonRx || false */
    };
  },

  validationSchema: _.formSchema,

  handleSubmit: (values, { props, setSubmitting, setFieldTouched, setFieldValue }) => {
    values.activeSection = values.activeSection + 1;
    console.log('handleSubmit ran');
    // For async call to set each field to not being touched, can be ignored
    setFieldValue('timeSubmitted', new Date());
    setTimeout(() => {
      if (values.activeSection === 1) {
        _.FirstStepValues.forEach(item => setFieldTouched(item, false));
      } else if (values.activeSection === 2) {
        _.SecondStepValues.forEach(item => setFieldTouched(item, false));
      }
    });

    // if (values.activeSection === 3) {
    //   var newDate = new Date();
    //   setFieldValue('timeSubmitted', newDate);
    // }

    if (values.activeSection === 4) {
      props.updateData([
        ...props.data,
        {
          'follower Name': `${values['firstName']} ${values['lastName']}`,
          MRN: `${values['mrn'] ? values['mrn'] : 'N/A'}`,
          status: 'Prescription sent',
          'Avg glucose': '-',
          'time in range': '-',
          rxComplete: 'true',
          timeSubmitted: `${values['timeSubmitted'].getUTCMonth() + 1}/${values['timeSubmitted'].getUTCDate()}/${values[
            'timeSubmitted'
          ].getUTCFullYear()}`,
          allSettings: values
        }
      ]);
    }
    // WHEN PRODUCTION READY, COMMENT OUT BELOW
    console.log(JSON.stringify(values, null, 2));

    // BACKEND CALLS TO STORE DATA WOULD GO HERE

    setSubmitting(false);
  },
  validateOnBlur: false,
  validateOnChange: false
})(FormBody);

export default EnhancedFormBody;
